import {
  AlertCircle,
  Badge as BadgeIcon,
  Building,
  Calendar,
  CalendarCheck,
  CalendarPlus,
  Check,
  CheckCheck,
  CheckCircle,
  CheckCircle2,
  ChevronDown,
  ChevronRight,
  Clock,
  Clock3,
  Copy,
  Database,
  DollarSign,
  Edit3,
  FileText,
  Folder,
  Link,
  Mail,
  MoreHorizontal,
  Palette,
  Phone,
  Play,
  Power,
  RefreshCw,
  RotateCcw,
  Settings,
  Star,
  Tag,
  Trash2,
  User,
  UserCheck,
  UserX,
  X,
  XCircle,
} from "lucide-react";
import { useState } from "react";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useAudit, useAuditSubscription } from "@/domains/audit/hooks";
import type { AuditFieldChange, FormattedFieldChange } from "@/domains/audit/types/audit-types";
import { formatAuditChanges } from "@/domains/audit/utils";

import { timezoneUtils } from "@/lib/timezone";
import { cn } from "@/lib/utils";
import { formatAuditDescription, getMetadataSubtitle } from "../utils/metadata-formatter";

interface AuditLogTimelineProps {
  salonId?: string | null;
  tableName?: string;
  tableNames?: string[];
  entityId?: string;
  dateRange?: {
    from: string;
    to: string;
  };
}

export function AuditLogTimeline({
  salonId,
  tableName,
  tableNames,
  entityId,
  dateRange: _dateRange,
}: AuditLogTimelineProps) {
  const [expandedEntries, setExpandedEntries] = useState<Set<string>>(new Set());
  const [copiedId, setCopiedId] = useState<string | null>(null);

  const resolvedTableNames = tableNames ?? (tableName ? [tableName] : undefined);

  // Use the new audit hook
  const { auditEntries, totalCount, loading, error, errorMessage, retry } = useAudit({
    salonId,
    limit: 50,
    tableNames: resolvedTableNames,
  });

  // Subscribe to real-time audit updates
  useAuditSubscription({
    salonId,
    entityId,
    tableName,
    enabled: true, // Enable real-time updates for audit page
  });

  // Use audit entries directly without grouping

  const toggleEntryExpansion = (entryId: string) => {
    setExpandedEntries((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(entryId)) {
        newSet.delete(entryId);
      } else {
        newSet.add(entryId);
      }
      return newSet;
    });
  };

  const isEntryExpanded = (entryId: string) => expandedEntries.has(entryId);

  const copyToClipboard = async (text: string, id: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedId(id);
      setTimeout(() => setCopiedId(null), 2000);
    } catch (err) {
      console.error("Failed to copy text: ", err);
    }
  };

  const getOperationIcon = (operation: string) => {
    switch (operation) {
      case "CREATE":
        return <Database className="w-4 h-4 text-green-500" />;
      case "UPDATE":
        return <Settings className="w-4 h-4 text-blue-500" />;
      case "DELETE":
        return <Database className="w-4 h-4 text-red-500" />;
      default:
        return <Database className="w-4 h-4 text-gray-500" />;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
      </div>
    );
  }

  if (error && !auditEntries?.length) {
    return (
      <div className="text-center py-8">
        <AlertCircle className="h-12 w-12 text-destructive mx-auto mb-4" />
        <h3 className="text-lg font-semibold mb-2">Unable to load audit logs</h3>
        <p className="text-muted-foreground mb-4">{errorMessage}</p>
        {retry && (
          <Button onClick={retry} variant="outline">
            <RefreshCw className="h-4 w-4 mr-2" />
            Try Again
          </Button>
        )}
      </div>
    );
  }

  return (
    <div>
      {/* Show error notification if there's an error but we have cached data */}
      {error && auditEntries?.length > 0 && (
        <Alert variant="destructive" className="mb-4">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{errorMessage}</AlertDescription>
        </Alert>
      )}

      {/* Timeline */}
      <div className="py-2">
        {!loading && (!auditEntries || auditEntries.length === 0) ? (
          <div className="text-center py-8 text-muted-foreground">
            No audit logs found for the selected criteria.
          </div>
        ) : (
          <div className="space-y-4">
            {(auditEntries || []).map((entry) => {
              const isExpanded = isEntryExpanded(entry.id);

              // Format changes using the new Prisma-based formatter system
              const { mainChanges, idFields: idChanges } = formatAuditChanges(
                (entry.changes as AuditFieldChange[]) || []
              );

              const showableChanges = isExpanded ? mainChanges : mainChanges.slice(0, 10);
              const hasMoreChanges = mainChanges.length > 10;

              // Check if we have metadata for enhanced display
              const hasMetadata = entry.metadata && Object.keys(entry.metadata).length > 0;
              const metadataDescription = hasMetadata ? formatAuditDescription(entry) : null;
              const _metadataSubtitle = hasMetadata ? getMetadataSubtitle(entry) : null;

              // Get appointment-specific icon for metadata entries
              const _getAppointmentIcon = () => {
                if (entry.tableName !== "appointment") return null;

                switch (entry.operation) {
                  case "INSERT":
                    return <CalendarPlus className="w-4 h-4 text-green-600" />;
                  case "UPDATE":
                    return <Edit3 className="w-4 h-4 text-blue-600" />;
                  case "DELETE":
                    return <Trash2 className="w-4 h-4 text-red-600" />;
                  default:
                    return <Calendar className="w-4 h-4 text-gray-600" />;
                }
              };

              return (
                <Card key={entry.id}>
                  <CardHeader className="pb-0 pt-3 px-4">
                    {/* Simple Title using metadata when available */}
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        {getOperationIcon(entry.operation)}
                        <h3 className="text-sm font-semibold">
                          {metadataDescription || entry.title}
                        </h3>
                      </div>

                      {/* Actions Menu */}
                      {entry.entityId && (
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                              <MoreHorizontal className="h-4 w-4" />
                              <span className="sr-only">Open menu</span>
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem
                              onClick={() => copyToClipboard(entry.entityId, entry.id)}
                            >
                              {copiedId === entry.id ? (
                                <>
                                  <CheckCheck className="mr-2 h-4 w-4" />
                                  Copied!
                                </>
                              ) : (
                                <>
                                  <Copy className="mr-2 h-4 w-4" />
                                  Copy Entity ID
                                </>
                              )}
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      )}
                    </div>
                  </CardHeader>

                  <CardContent className="pt-0 pb-3 px-4 space-y-2">
                    {/* Field Changes */}
                    <div className="space-y-2">
                      {showableChanges.map((change: FormattedFieldChange, index) => {
                        // Get icon component from Lucide
                        const getIconComponent = (iconName: string | any) => {
                          // If it's already a React component, return it
                          if (typeof iconName === "function") {
                            return iconName;
                          }

                          // Otherwise map string to icon
                          switch (iconName) {
                            case "dollar-sign":
                              return DollarSign;
                            case "user":
                              return User;
                            case "user-check":
                              return UserCheck;
                            case "star":
                              return Star;
                            case "calendar":
                              return Calendar;
                            case "calendar-check":
                              return CalendarCheck;
                            case "clock":
                              return Clock;
                            case "mail":
                              return Mail;
                            case "phone":
                              return Phone;
                            case "badge":
                              return BadgeIcon;
                            case "palette":
                              return Palette;
                            case "check-circle":
                              return CheckCircle;
                            case "x-circle":
                              return XCircle;
                            case "link":
                              return Link;
                            case "power":
                              return Power;
                            case "tag":
                              return Tag;
                            case "folder":
                              return Folder;
                            case "building":
                              return Building;
                            // Additional icons for appointment status
                            case Check:
                              return Check;
                            case X:
                              return X;
                            case Clock3:
                              return Clock3;
                            case CheckCircle2:
                              return CheckCircle2;
                            case UserX:
                              return UserX;
                            case RotateCcw:
                              return RotateCcw;
                            case Play:
                              return Play;
                            default:
                              return FileText;
                          }
                        };

                        // Handle custom React component icons from appointment status
                        const IconComponent = change.customIcon?.component
                          ? change.customIcon.component
                          : getIconComponent(change.icon);

                        // Get icon color class for status fields and custom icons
                        const getIconColor = () => {
                          // Custom icon with className (appointment status/outcome)
                          if (change.customIcon?.className) {
                            return change.customIcon.className;
                          }

                          // Legacy metadata styling
                          if (change.newValueMetadata?.getStyle) {
                            const style = change.newValueMetadata.getStyle();
                            return style.iconColor || "text-gray-600 dark:text-gray-400";
                          }

                          return "text-gray-600 dark:text-gray-400";
                        };

                        return (
                          <div
                            key={`${entry.id}-${change.field}-${index}`}
                            className="flex items-start gap-3 py-1"
                          >
                            <div className={cn("mt-0.5", getIconColor())}>
                              <IconComponent className="w-4 h-4" />
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="text-sm">
                                <span className="font-medium">{change.displayName}:</span>
                                <span className="ml-2 inline-flex items-center gap-1">
                                  {/* Old value with indicator (color or status icon) */}
                                  {change.oldColorIndicator &&
                                    (change.oldColorIndicator.type === "status-icon" ? (
                                      <change.oldColorIndicator.component
                                        className={cn(
                                          "w-3 h-3",
                                          change.oldColorIndicator.className
                                        )}
                                      />
                                    ) : (
                                      <span
                                        className={cn(
                                          "w-3 h-3 rounded-full",
                                          change.oldColorIndicator.className
                                        )}
                                        style={change.oldColorIndicator.style}
                                      />
                                    ))}
                                  <span>{change.formattedOldValue}</span>

                                  <span className="mx-2 text-muted-foreground">→</span>

                                  {/* New value with indicator (color or status icon) */}
                                  {change.newColorIndicator &&
                                    (change.newColorIndicator.type === "status-icon" ? (
                                      <change.newColorIndicator.component
                                        className={cn(
                                          "w-3 h-3",
                                          change.newColorIndicator.className
                                        )}
                                      />
                                    ) : (
                                      <span
                                        className={cn(
                                          "w-3 h-3 rounded-full",
                                          change.newColorIndicator.className
                                        )}
                                        style={change.newColorIndicator.style}
                                      />
                                    ))}
                                  <span>{change.formattedNewValue}</span>
                                </span>
                              </div>
                            </div>
                          </div>
                        );
                      })}

                      {/* Expand/Collapse Button for more changes */}
                      {hasMoreChanges && (
                        <button
                          type="button"
                          onClick={() => toggleEntryExpansion(entry.id)}
                          className="flex items-center gap-1 text-sm text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300 mt-2"
                        >
                          {isExpanded ? (
                            <>
                              <ChevronDown className="w-4 h-4" />
                              Show fewer changes
                            </>
                          ) : (
                            <>
                              <ChevronRight className="w-4 h-4" />
                              Show {mainChanges.length - 10} more changes
                            </>
                          )}
                        </button>
                      )}
                    </div>

                    {/* Footer */}
                    <div className="flex flex-col gap-2 pt-2 border-t">
                      {/* ID changes */}
                      {idChanges.length > 0 && (
                        <div className="flex flex-wrap gap-3 text-xs text-muted-foreground">
                          {idChanges.map((idChange, idx) => (
                            <div key={`${entry.id}-id-${idx}`} className="flex items-center gap-1">
                              <span className="font-medium">{idChange.displayName}:</span>
                              <span>
                                {idChange.formattedOldValue} → {idChange.formattedNewValue}
                              </span>
                            </div>
                          ))}
                        </div>
                      )}

                      {/* Metadata */}
                      <div className="flex items-center gap-4 text-xs text-muted-foreground">
                        <div className="flex items-center gap-1">
                          <User className="w-3 h-3" />
                          {entry.user?.fullName || entry.user?.email || "System"}
                        </div>
                        <div className="flex items-center gap-1">
                          <Calendar className="w-3 h-3" />
                          <span
                            title={
                              entry.timestamp
                                ? timezoneUtils
                                    .fromISO(entry.timestamp)
                                    .toFormat("MMM d, yyyy h:mm a (ZZZZ)")
                                : undefined
                            }
                          >
                            {entry.timestamp
                              ? timezoneUtils
                                  .fromISO(entry.timestamp)
                                  .toFormat("MMM d, yyyy h:mm a")
                              : "Unknown"}
                          </span>
                        </div>
                        {entry.ipAddress && <span className="text-xs">IP: {entry.ipAddress}</span>}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </div>

      {/* Show total count */}
      {totalCount > 0 && (
        <div className="mt-6 text-center text-sm text-muted-foreground">
          Showing {auditEntries?.length || 0} of {totalCount} audit logs
        </div>
      )}
    </div>
  );
}
